import SvgIcon from '@home/components/SvgIcon';
import GPT from '@op/components/GPT';
import {
  GptOptionsType,
  GPTType,
  PromptConfirmItemType,
} from '@op/components/GPT/types';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import React, { useMemo } from 'react';
import { Button, Checkbox, CheckChangeContext, Form } from 'tea-component';
import { useIntl } from 'umi';
import {
  GPTContentType,
  KeywordUrlTabType,
  PromptKeyType,
} from '../../../../types';
import BubbleCommon, {
  aigcGPTOptionsTips,
  keywordDrillUrlCommentsGptTips,
  keywordDrillUrlVideoContentSummary,
  TipsMapType,
} from '@op/components/BubbleCommon';
import {
  gptOptionsShortTextMap,
  gptOptionsTextMap,
} from '@op/components/GPT/constants';

type GPTOptionsType = {
  remainder: number;
  total: number;
  toggleVisible: (tab: GPTContentType, isAdvanced?: boolean) => void;
  videoPromptInfo?: PromptConfirmItemType;
  commentsPromptInfo?: PromptConfirmItemType;
  keywordPromptInfo?: PromptConfirmItemType;
  setPromptInfo: (
    value: PromptConfirmItemType,
    tab: Array<PromptKeyType> | PromptKeyType,
  ) => void;
  advancedLimitLoading: boolean;
  gptList: GPTContentType[];
  setGptList: (value: GPTContentType[]) => void;
  gptType: GPTType;
};

function BasicNear({
  toggleVisible,
  formatMessage,
  tab,
  gptType,
  text = 'Prompt',
}: {
  toggleVisible: any;
  formatMessage: any;
  tab: PromptKeyType;
  gptType: GPTType;
  text?: string;
}) {
  return (
    <Button
      type="link"
      onClick={() => {
        toggleVisible(tab);
        buriedBtnPoint('EditPrompt', gptType, tab, 'Basic');
      }}
      style={{ marginLeft: '8px' }}
    >
      <SvgIcon type="pencil-m" />
      {formatMessage({ id: text })}
    </Button>
  );
}

function BasicGptOptions(props: {
  formatMessage: any;
  toggleVisible: any;
  tab: PromptKeyType;
  gptType: GPTType;
  shortText?: boolean;
}) {
  const gptOptionsMap = props.shortText
    ? gptOptionsShortTextMap
    : gptOptionsTextMap;
  return (
    <>
      {props.formatMessage({ id: gptOptionsMap.basic })}
      <BasicNear {...props}></BasicNear>
    </>
  );
}

function FormItemBox({ children }: { children: any }) {
  const { formatMessage } = useIntl();
  return (
    <Form.Item
      label={
        <div className="item-label">
          {formatMessage({ id: 'AI Options' })}
          <span style={{ verticalAlign: '-2px' }}>
            <BubbleCommon
              tipsMap={aigcGPTOptionsTips}
              placement="top"
            ></BubbleCommon>
          </span>
        </div>
      }
    >
      {children}
    </Form.Item>
  );
}

export default function GptOptions({
  tab,
  gptType,
  setGptType,
  ...props
}: GPTOptionsType & {
  tab: KeywordUrlTabType;
  setGptType: (value: GPTType) => void;
}) {
  const GptAgent = useMemo(() => {
    return (
      <GPT.AgentOptions
        gptType={gptType}
        setGptType={(value) => {
          if (value === 'deepseek') {
            props.setPromptInfo({ gptOptions: 'basic' }, [
              'comments',
              'video',
              'keyword',
            ]);
          }
          setGptType(value);
        }}
      ></GPT.AgentOptions>
    );
  }, [gptType]);

  return (
    <>
      {tab === 'keyword' ? (
        <GptOptionsKeyword
          {...props}
          // GptAgentElement={GptAgent}
          gptType={gptType}
        ></GptOptionsKeyword>
      ) : (
        <GptOptionsUrl
          {...props}
          gptType={gptType}
          // GptAgentElement={GptAgent}
        ></GptOptionsUrl>
      )}
    </>
  );
}

function GptOptionsUrl({
  remainder,
  total,
  toggleVisible,
  videoPromptInfo,
  commentsPromptInfo,
  setPromptInfo,
  advancedLimitLoading,
  gptList,
  setGptList,
  GptAgentElement,
  gptType,
}: GPTOptionsType & {
  GptAgentElement?: any;
}) {
  const { formatMessage } = useIntl();

  const topOptions: {
    text: string;
    value: GPTContentType;
    endTips?: string;
    tipsMap: TipsMapType;
  }[] = useMemo(() => {
    return [
      {
        text: 'Comments Summary',
        value: 'comments',
        tipsMap: keywordDrillUrlCommentsGptTips,
      },
      {
        text: 'Video Content Summary',
        value: 'video',
        endTips: '(Only supports videos from certain channels)',
        tipsMap: keywordDrillUrlVideoContentSummary(),
      },
    ];
  }, []);

  return (
    <FormItemBox>
      <div className="dbo-gpt-top-options-box">
        {GptAgentElement}
        <Checkbox.Group
          value={gptList}
          onChange={(value: any, context: CheckChangeContext) => {
            setGptList(value);
            const pointKey =
              context.check.name === 'video'
                ? 'SelectVideoSummary'
                : 'SelectCommentSummary';
            buriedBtnPoint(pointKey, gptType);
          }}
        >
          {topOptions.map(({ text, value, endTips, tipsMap }) => {
            const promptInfo =
              value === 'comments' ? commentsPromptInfo : videoPromptInfo;
            return (
              <div className="dbo-gpt-top-options-box-item" key={value}>
                <BubbleCommon
                  placement="top"
                  key={value}
                  tipsMap={tipsMap}
                  showIcon={false}
                >
                  <Checkbox key={value} name={value}>
                    <span className="dbo-gpt-options-line">
                      {formatMessage({ id: text })}
                    </span>
                    {endTips && (
                      <span className="dbo-form-tips-gray">
                        {formatMessage({ id: endTips })}
                      </span>
                    )}
                  </Checkbox>
                </BubbleCommon>
                {value === 'video' ? (
                  <>
                    <BasicGptOptions
                      toggleVisible={toggleVisible}
                      tab={value}
                      formatMessage={formatMessage}
                      gptType={gptType}
                      shortText
                    ></BasicGptOptions>
                  </>
                ) : (
                  <GPT.GPTOptions
                    remainder={remainder}
                    total={total}
                    basicNearElement={
                      <BasicNear
                        toggleVisible={toggleVisible}
                        tab={value}
                        gptType={gptType}
                        formatMessage={formatMessage}
                      ></BasicNear>
                    }
                    advancedNearElement={
                      <BasicNear
                        toggleVisible={(tab: GPTContentType) =>
                          toggleVisible(tab, true)
                        }
                        formatMessage={formatMessage}
                        gptType={gptType}
                        tab="comments"
                        text="Analysis Target"
                      ></BasicNear>
                    }
                    shortText
                    gptOptions={promptInfo?.gptOptions as GptOptionsType}
                    setGptOptions={(val) => {
                      buriedBtnPoint('GPT_Options', gptType, value, val);
                      setPromptInfo({ gptOptions: val }, value);
                    }}
                    loading={advancedLimitLoading}
                  />
                )}
              </div>
            );
          })}
        </Checkbox.Group>
      </div>
    </FormItemBox>
  );
}

function GptOptionsKeyword({
  remainder,
  total,
  toggleVisible,
  keywordPromptInfo,
  setPromptInfo,
  advancedLimitLoading,
  GptAgentElement,
  gptType,
}: GPTOptionsType & {
  GptAgentElement?: any;
}) {
  const { formatMessage } = useIntl();

  return (
    <GPT.GPTOptionsForm
      remainder={remainder}
      total={total}
      basicNearElement={
        <BasicNear
          toggleVisible={toggleVisible}
          formatMessage={formatMessage}
          gptType={gptType}
          tab="keyword"
        ></BasicNear>
      }
      advancedNearElement={
        <BasicNear
          toggleVisible={(tab: GPTContentType) => toggleVisible(tab, true)}
          formatMessage={formatMessage}
          gptType={gptType}
          tab="keyword"
          text="Analysis Target"
        ></BasicNear>
      }
      topElement={GptAgentElement}
      gptOptions={keywordPromptInfo?.gptOptions as GptOptionsType}
      setGptOptions={(value) => setPromptInfo({ gptOptions: value }, 'keyword')}
      loading={advancedLimitLoading}
    />
  );
}
